import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import React from "react";

import { RobotoButton } from "@/shared/components";
import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import { WebhookIntegrationRecord } from "@/shared/domain/integrations";
import { useUpdateWebhookIntegrationSecret } from "@/shared/domain/integrations/hooks/useUpdateWebhookIntegrationSecret";

import styles from "./FoxgloveIntegrationStepper.module.css";

export interface ConfigureWebhookContentProps {
  foxgloveIntegration: WebhookIntegrationRecord | null | undefined;
}

export function ConfigureWebhookContent({
  foxgloveIntegration,
}: ConfigureWebhookContentProps) {
  const [webhookSecret, setWebhookSecret] = React.useState("");
  const [copySuccess, _] = React.useState(false);
  const updateSecretMutation = useUpdateWebhookIntegrationSecret();

  // Determine if we need event.created based on webhook_events
  const includeEventCreated =
    foxgloveIntegration?.webhook_events?.includes("event.created") ?? false;

  const handleUpdateSecret = () => {
    const trimmedSecret = webhookSecret.trim();
    if (!foxgloveIntegration?.webhook_endpoint_id || !trimmedSecret) {
      return;
    }

    updateSecretMutation.mutate(
      {
        webhookEndpointId: foxgloveIntegration.webhook_endpoint_id,
        webhookSecret: trimmedSecret,
      },
      {
        onSuccess: () => {
          setWebhookSecret("");
        },
      },
    );
  };

  return (
    <>
      <Typography variant="body2" className={styles.marginBottom2}>
        Now you need to register this webhook in your Foxglove account and copy
        the webhook secret back here.
      </Typography>

      {foxgloveIntegration?.webhook_endpoint_url && (
        <Box className={styles.marginBottom3}>
          <Typography
            variant="body2"
            className={classNames(
              styles.marginBottom1,
              styles.mediumFontWeight,
            )}
          >
            Webhook URL to register in Foxglove:
          </Typography>
          <TextField
            fullWidth
            value={foxgloveIntegration.webhook_endpoint_url}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CopyToClipboardButton
                    text={foxgloveIntegration.webhook_endpoint_url}
                  />
                </InputAdornment>
              ),
            }}
            className={styles.webhookUrlInput}
          />
          {copySuccess && (
            <Typography variant="caption" color="success.main">
              ✓ Copied to clipboard!
            </Typography>
          )}
        </Box>
      )}

      <Box className={styles.marginBottom2}>
        <Typography variant="body2" className={styles.marginBottom1}>
          1. Navigate to{" "}
          <Link
            href="https://app.foxglove.dev/~/settings/webhooks"
            target="_blank"
            rel="noopener noreferrer"
          >
            Foxglove Webhook Settings
          </Link>
        </Typography>
        <Typography variant="body2" className={styles.marginBottom1}>
          2. Add a new webhook with the URL above, when prompted for{" "}
          <b>Triggering events</b>, check{" "}
          <Chip
            label="recording.imported"
            size="small"
            color="primary"
            variant="outlined"
            className={styles.foxgloveEventChip}
          />
          {includeEventCreated && (
            <>
              {" "}
              and{" "}
              <Chip
                label="event.created"
                size="small"
                color="primary"
                variant="outlined"
                className={styles.foxgloveEventChip}
              />
            </>
          )}
        </Typography>
        <Typography variant="body2" className={styles.marginBottom2}>
          3. Copy the webhook <b>Secret</b> generated by Foxglove and paste it
          below.
        </Typography>
      </Box>

      <Box className={styles.marginBottom2}>
        <TextField
          fullWidth
          label="Webhook Secret"
          type="password"
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          placeholder="Paste your Foxglove webhook secret here"
          className={styles.marginBottom2}
        />
        <RobotoButton
          variant="contained"
          onClick={handleUpdateSecret}
          disabled={!webhookSecret.trim() || updateSecretMutation.isPending}
          startIcon={
            updateSecretMutation.isPending ? (
              <CircularProgress size={16} />
            ) : null
          }
          eventName="UpdateFoxgloveWebhookSecretClicked"
        >
          {updateSecretMutation.isPending
            ? "Registering..."
            : "Register Secret"}
        </RobotoButton>
      </Box>

      {updateSecretMutation.isError && (
        <Alert severity="error" className={styles.marginBottom2}>
          Failed to update webhook secret. Please try again.
        </Alert>
      )}
    </>
  );
}
